import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Video, Hash, MessageSquare, ArrowRight } from "lucide-react";

const sectors = [
  "IT & Software", "Journalism", "Construction", "Sales", "Hospitality & Cooking",
  "Household Help", "Healthcare", "Design", "Education", "Logistics", "Finance", "Retail",
];

export default function Landing() {
  return (
    <div className="bg-[#FAF9F6]">
      {/* HERO */}
      <section className="vc-grain border-b border-[#E2E4E2]">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#D96C5B] font-semibold mb-6" data-testid="hero-overline">
              A serious visual resume platform
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#1A1E1C]">
              Your work, in <span className="text-[#1E3F33]">90 seconds</span>.
              <br />Not in a 4-page PDF.
            </h1>
            <p className="mt-6 text-lg text-[#68706B] max-w-xl leading-relaxed">
              VisualCV lets every kind of professional — from software engineers to cooks,
              builders to household helpers — introduce themselves with a short video.
              Recruiters discover you organically. No noise, no propaganda, no entertainment.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="rounded-none bg-[#1E3F33] hover:bg-[#152C24] h-12 px-6 text-base" data-testid="hero-cta-primary">
                Create your visual resume <ArrowRight className="ml-2 w-4 h-4" />
              </Button></Link>
              <Link to="/register?role=hr"><Button size="lg" variant="outline" className="rounded-none border-[#1E3F33] text-[#1E3F33] hover:bg-[#E8ECEB] h-12 px-6 text-base" data-testid="hero-cta-hr">
                I&apos;m hiring
              </Button></Link>
            </div>
            <div className="mt-10 flex items-center gap-2 text-xs text-[#68706B]">
              <ShieldCheck className="w-4 h-4 text-[#1E3F33]" />
              AI-moderated. No politics, no propaganda, no entertainment uploads.
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="relative aspect-[3/4] bg-[#1E3F33] overflow-hidden">
              <img alt="" src="https://images.pexels.com/photos/7597737/pexels-photo-7597737.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700" className="absolute inset-0 w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-2">#design #frontend</div>
                <div className="text-2xl font-bold tracking-tight">Anika, 5y Product Designer</div>
                <div className="text-sm opacity-80 mt-1">Bengaluru • Looking for senior roles</div>
              </div>
              <div className="absolute top-4 right-4 bg-white/95 backdrop-blur px-2 py-1 text-xs font-semibold flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-[#1E3F33] rounded-full" /> LIVE
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE SECTORS */}
      <section className="border-b border-[#E2E4E2] bg-[#F0EFEB] py-6 overflow-hidden">
        <div className="vc-marquee gap-12 px-12">
          {[...sectors, ...sectors].map((s, i) => (
            <span key={`${s}-${i}`} className="text-sm uppercase tracking-[0.2em] text-[#1E3F33] font-semibold whitespace-nowrap">
              {s}  •
            </span>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", icon: Video, t: "Record a short intro", d: "15–60 seconds. Show your craft — code, cook, weld, write, sell." },
            { n: "02", icon: Hash, t: "Tag your strengths", d: "Hashtags surface you to the right hiring managers across sectors." },
            { n: "03", icon: MessageSquare, t: "Recruiters DM you", d: "When they like what they see, they ask for your resume directly." },
          ].map((s) => (
            <div key={s.n} className="bg-white border border-[#E2E4E2] p-8 hover:-translate-y-1 transition-all duration-200">
              <div className="text-xs font-mono text-[#D96C5B] tracking-widest">{s.n}</div>
              <s.icon className="w-7 h-7 mt-4 text-[#1E3F33]" strokeWidth={1.5} />
              <h3 className="text-xl font-bold mt-4 tracking-tight">{s.t}</h3>
              <p className="text-sm text-[#68706B] mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* INTEGRITY */}
      <section className="bg-[#1E3F33] text-white">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#D96C5B] font-semibold">Integrity first</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 leading-tight">
              We delete the noise so serious candidates rise.
            </h2>
          </div>
          <div className="space-y-4 text-white/85">
            <p>Every upload is reviewed by GPT-5.2. Entertainment skits, political content, propaganda, advertisements — instantly flagged.</p>
            <p>Human moderators review every report. Repeated violations are banned.</p>
            <p>Recruiters get one feed: <span className="text-[#D96C5B] font-semibold">real candidates, organically presented</span>.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E4E2] py-10 text-center text-xs text-[#68706B]">
        © {new Date().getFullYear()} VisualCV — Built for serious work.
      </footer>
    </div>
  );
}
